// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { useSupportError } from '@/hooks/use-support-error';
import {
  type ShipmentDataUploadResult,
  uploadShipmentData,
  uploadShipmentDataResponseSchema,
} from '@/shipment/api/upload-shipment-data';
import { CsvUpload } from '@/shipment/components/shipment-data-upload/csv-upload';
import { Results } from '@/shipment/components/shipment-data-upload/results';
import { Stepper } from '@/shipment/components/shipment-data-upload/stepper';
import { TemplateDownload } from '@/shipment/components/shipment-data-upload/template-download';
import type {
  ExpandedShipmentsState,
  Step,
} from '@/shipment/components/shipment-data-upload/types';
import { shipmentsQueryKey } from '@/shipment/hooks/use-shipments';
import { Box, CardContent } from '@mui/material';
import { captureException, withScope } from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { ChangeEvent, useCallback, useRef, useState } from 'react';

interface ShipmentDataUploadProps {
  onClose: () => void;
}

export function ShipmentDataUpload(props: ShipmentDataUploadProps) {
  const queryClient = useQueryClient();
  const { onClose } = props;
  const { showSupportError } = useSupportError();
  const [currentStep, setCurrentStep] = useState<Step>('download-template');
  const [file, setFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] =
    useState<ShipmentDataUploadResult | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(
    null
  );
  const [uploadLineCount, setUploadLineCount] = useState<number | undefined>(
    undefined
  );
  const [expandedShipments, setExpandedShipments] =
    useState<ExpandedShipmentsState>({
      updated: false,
      failed: false,
      structureErrors: false,
    });
  const [hasFatalError, setHasFatalError] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const clearFile = useCallback(() => {
    setFile(null);
    setUploadLineCount(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleSelectFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        setFile(selectedFile);
        setUploadErrorMessage(null);

        const reader = new FileReader();
        reader.onload = (e) => {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter((line) => line.trim() !== '');
          setUploadLineCount(Math.max(0, lines.length - 1));
        };
        reader.readAsText(selectedFile);
      }
    },
    []
  );

  const handleUploadResult = useCallback(
    (result: ShipmentDataUploadResult) => {
      clearFile();
      setUploadResult(result);
      setUploadErrorMessage(null);
      setCurrentStep('results');
      if (result.success) {
        void queryClient.invalidateQueries({ queryKey: shipmentsQueryKey });
      }
    },
    [clearFile, queryClient]
  );

  const { mutate: uploadShipment, isPending: isUploading } = useMutation({
    mutationFn: async () => {
      if (!file) {
        throw new Error('Missing file selection');
      }
      const formData = new FormData();
      formData.append('data', file);
      return uploadShipmentData(formData);
    },
    onSuccess: (result) => {
      handleUploadResult(result);
    },
    onError: (error: AxiosError) => {
      clearFile();

      const knownError = uploadShipmentDataResponseSchema.safeParse(
        error.response?.data
      );
      if (knownError.success) {
        const result = knownError.data.data;
        const hasContent =
          result.items.length > 0 ||
          result.structureErrors.length > 0 ||
          result.rowErrors.length > 0;
        if (hasContent) {
          handleUploadResult(result);
          return;
        }
      }

      const apiError = handleAPIError(error);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated !== SUPPORT_FALLBACK_ERROR_MESSAGE) {
        setUploadErrorMessage(curated);
        return;
      }

      if (!apiError.sentToSentry) {
        withScope((scope) => {
          scope.setContext('upload', {
            fileSize: file?.size,
            lineCount: uploadLineCount,
          });
          captureException(error);
        });
      }
      setHasFatalError(true);
      showSupportError('An error occurred while uploading shipment data.');
    },
  });

  const handleUpload = useCallback(() => {
    if (file) {
      setUploadErrorMessage(null);
      uploadShipment();
    }
  }, [file, uploadShipment]);

  const handleToggleExpanded = useCallback(
    (section: keyof ExpandedShipmentsState) => {
      setExpandedShipments((prev) => ({
        ...prev,
        [section]: !prev[section],
      }));
    },
    []
  );

  const handleStartNew = useCallback(() => {
    setCurrentStep('download-template');
    clearFile();
    setUploadResult(null);
    setUploadErrorMessage(null);
    setExpandedShipments({
      updated: false,
      failed: false,
      structureErrors: false,
    });
  }, [clearFile]);

  const handleBack = useCallback(() => {
    if (currentStep === 'upload-csv') {
      setCurrentStep('download-template');
      setHasFatalError(false);
    } else if (currentStep === 'results') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 'download-template') {
      setCurrentStep('upload-csv');
    }
  }, [currentStep]);

  return (
    <Box>
      <CardContent>
        <Stepper currentStep={currentStep} />

        {currentStep === 'download-template' && (
          <TemplateDownload onBack={onClose} onNext={handleNext} />
        )}

        {currentStep === 'upload-csv' && (
          <CsvUpload
            uploadErrorMessage={uploadErrorMessage}
            file={file}
            isUploading={isUploading}
            onSelectFile={handleSelectFile}
            onUpload={handleUpload}
            onBack={handleBack}
            onClose={onClose}
            hasFatalError={hasFatalError}
            uploadLineCount={uploadLineCount}
          />
        )}

        {currentStep === 'results' && (
          <Results
            uploadResult={uploadResult}
            expandedShipments={expandedShipments}
            onToggleExpanded={handleToggleExpanded}
            onBack={handleBack}
            onStartNew={handleStartNew}
            onClose={onClose}
          />
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept=".csv"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </CardContent>
    </Box>
  );
}
